package dev.lingvo.site.nav

import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast

/**
 * Somewhere the shell can send you, and the role that may go there.
 *
 * The gate lives beside the destination rather than in an `if` at the call site because the same
 * list is read by the sidebar, the settings tabs and — later — the command palette. Three copies of
 * "is this person an admin?" is three chances for one of them to be wrong, and the one that is wrong
 * is the one that offers a translator a button the API refuses.
 */
data class AppDestination(
    val id: String,
    val label: String,
    val path: String,
    /** The weakest role that may reach it. */
    val minimumRole: Role,
    /**
     * The mark drawn beside [label], in every rail, pill row and palette row.
     *
     * **Non-null and without a default, so a destination cannot be added without one.** The mark
     * used to be decided by a `when (id)` in the shell with an `else -> globe` fallback; the project
     * rail then added eight destinations, every one of them missed the `when`, and the whole rail
     * rendered eight identical globes carrying no information at all. A comment warned about
     * exactly that and did not stop it. Moving the choice into the record makes the compiler the
     * thing that stops it: a destination with no mark does not build.
     */
    val glyph: NavGlyph,
    /**
     * The catalogue key for [label], when [id] cannot serve as one.
     *
     * Ids are unique within a list, not across the file, and two of them repeat with **different
     * wording**: `general` is "Workspace" in the settings rail and "General" as a project tab, and
     * `providers` is "Providers" against "Translation providers". The catalogue is a flat map keyed
     * `nav.<key>`, so the second declaration overwrote the first and every translated locale showed
     * the project tab's wording in the workspace rail. English never revealed it, because a missing
     * catalogue falls back to the call site's own label.
     *
     * Renaming the ids was the wrong fix: they are the `?tab=` names `resolveProjectTab` returns and
     * `Settings.kt` switches on. The key is a separate concern from the route, so it is a separate
     * field, and `extract-strings.mjs` now refuses a collision rather than resolving it silently.
     */
    val catalogueKey: String = id,
)

/**
 * What the shell offers, decided by role rather than hardcoded.
 *
 * Nothing here is a security boundary — every route re-checks on arrival and the API refuses
 * regardless. It decides only what is *drawn*, and that matters for a different reason: an interface
 * that offers what the server refuses reads as a broken product, and a translator who sees Billing,
 * Danger zone and Providers has no way to know which of them are actually theirs.
 */
object AppNav {

    private val PRIMARY = listOf(
        // First, and the landing page for translators and viewers: "what should I do now" is the
        // question they open Lingvo to answer, and a project list makes them navigate to it.
        AppDestination("work", "Your work", "/app/work", Role.VIEWER, NavGlyph.ListChecks),
        // The "see all" behind the queue's Mentions section, never a competing queue.
        AppDestination("inbox", "Inbox", "/app/inbox", Role.VIEWER, NavGlyph.Mail),
        // **Admin, not viewer.** It was VIEWER, and [landingFor] sends translators and viewers to
        // `/app/work` — so the sidebar offered them a Dashboard that redirected away the instant
        // they pressed it. An item that always bounces is an item that always denies, wearing a
        // redirect. The dashboard aggregates readiness, credits and spend across the workspace,
        // which is the same bar as billing.
        AppDestination("dashboard", "Dashboard", "/app", Role.ADMIN, NavGlyph.Grid),
        AppDestination("projects", "Projects", "/app/projects", Role.VIEWER, NavGlyph.Folder),
        // **Reachable at last.** `/app/activity` is the workspace event log and no rail, pill row or
        // palette row pointed at it — it existed and could be opened only by typing the URL. It is
        // VIEWER because it answers "what happened here", which is orientation: it names events, not
        // money, and every row it draws is already visible to whoever can see the project.
        AppDestination("activity", "Activity", "/app/activity", Role.VIEWER, NavGlyph.History),
        // Throughput, spend and the finish-date forecast. Admin, because it aggregates what the
        // workspace is paying as well as what it is producing — the same bar as billing.
        AppDestination("insights", "Insights", "/app/insights", Role.ADMIN, NavGlyph.Chart),
    )

    /**
     * Where a role lands when it opens the app.
     *
     * Translators and viewers go to the queue: the dashboard is a management surface, and sending
     * somebody to a page whose numbers are not their business is how "where do I start?" becomes a
     * support question.
     */
    fun landingFor(role: Role?): String = when (role) {
        Role.TRANSLATOR, Role.VIEWER -> "/app/work"
        else -> "/app"
    }

    /**
     * Settings, in the order they are offered.
     *
     * The sidebar's Settings link used to be the literal string `/app/settings/tokens`. That is a
     * personal page, so it happened to be reachable by everyone — but only by accident, and the
     * moment a workspace-level settings page is added the hardcoded link either sends a translator
     * somewhere they will be refused or hides a page an owner needs. Resolving it through
     * [settingsLandingFor] means adding a page is one line here.
     */
    private val SETTINGS = listOf(
        // Members first: it is the workspace page people look for, and the one an owner opens on
        // their first day with a colleague waiting to be let in.
        // Everyone may see which workspace they are in — that is orientation, not management.
        AppDestination("general", "Workspace", "/app/settings/general", Role.VIEWER, NavGlyph.Box),
        // Which workspaces you are in at all, which is orientation rather than management: somebody
        // invited to two of them needs to know both exist before anything else makes sense.
        AppDestination("workspaces", "All workspaces", "/app/workspaces", Role.VIEWER, NavGlyph.Grid),
        AppDestination("members", "Members", "/app/settings/members", Role.VIEWER, NavGlyph.Users),
        // Money. A translator has no business seeing what the workspace pays, and the API refuses
        // them anyway — `requireManage` on /orgs/{slug}/billing.
        AppDestination("billing", "Billing", "/app/settings/billing", Role.ADMIN, NavGlyph.CreditCard),
        // **The hub linked here and the model did not know it existed**, so the pill row above the
        // page was the billing group without the page you were standing on, and the palette could
        // not offer it at all. Same for approvals, budgets, notifications and close below: five
        // pages the settings index linked to that nothing else in the product could reach.
        AppDestination("seats", "Seats", "/app/settings/billing/seats", Role.ADMIN, NavGlyph.Users),
        // Provider keys are credentials and spend. Same bar as billing.
        AppDestination("providers", "Providers", "/app/settings/providers", Role.ADMIN, NavGlyph.Languages),
        AppDestination("tokens", "API tokens", "/app/settings/tokens", Role.VIEWER, NavGlyph.Key),
        // Names people and what they were paid to be able to do, so it follows billing's bar rather
        // than the token page's.
        AppDestination("audit", "Audit log", "/app/settings/audit", Role.ADMIN, NavGlyph.ScrollText),
        AppDestination("webhooks", "Webhooks", "/app/settings/webhooks", Role.ADMIN, NavGlyph.Webhook),
        AppDestination("agents", "Agents", "/app/settings/agents", Role.ADMIN, NavGlyph.Bot),
        // Agents asking to spend past their budget. Admin, because approving one spends money.
        AppDestination("approvals", "Spend requests", "/app/settings/agents/approvals", Role.ADMIN, NavGlyph.SquareCheck),
        // Spend, so the same bar as billing.
        AppDestination("usage", "Usage", "/app/settings/usage", Role.ADMIN, NavGlyph.Coins),
        // What can cost money while nobody is looking.
        AppDestination("budgets", "Budgets", "/app/settings/usage/budgets", Role.ADMIN, NavGlyph.Receipt),
        AppDestination("spend", "Spend", "/app/reports/spend", Role.ADMIN, NavGlyph.ChartLine),
        // Yours regardless of role: the devices signed in as you, and leaving.
        // The account entry point. Security, notifications and closing hang off it rather than
        // each claiming a line in a menu that is already long.
        AppDestination("account", "Your account", "/app/account", Role.VIEWER, NavGlyph.User),
        AppDestination("security", "Security", "/app/account/security", Role.VIEWER, NavGlyph.Lock),
        AppDestination("notifications", "Notifications", "/app/account/notifications", Role.VIEWER, NavGlyph.Bell),
        // Leaving for good. Yours alone, so VIEWER — an owner cannot close somebody else's account
        // from here, and the page itself refuses an owner who would strand the workspace.
        AppDestination("close", "Close your account", "/app/account/close", Role.VIEWER, NavGlyph.Trash),
        // Where somebody goes when it is not working. Every role, because everyone can be stuck.
        AppDestination("help", "Help", "/app/help", Role.VIEWER, NavGlyph.Help),
    )

    /**
     * Project settings tabs.
     *
     * Every one of these is a management surface, so a translator sees none of them — they used to
     * see seven tabs and could act on none.
     *
     * Members is deliberately absent: it moved to `/app/settings/members`, where it belongs. It is
     * the same list whichever project you opened it from, and showing it under a project made access
     * look like something a project owns.
     */
    private val PROJECT_SETTINGS = listOf(
        // `catalogueKey` stays the **last** argument of every declaration that carries one:
        // `extract-strings.mjs` reads this file with a regex that only sees the key when it closes
        // the call, and a key it cannot see is a label two destinations then silently share.
        AppDestination("general", "General", "?tab=general", Role.ADMIN, NavGlyph.Wrench, catalogueKey = "project_general"),
        AppDestination("languages", "Languages", "?tab=languages", Role.ADMIN, NavGlyph.Languages),
        AppDestination("files", "Files", "?tab=files", Role.ADMIN, NavGlyph.Files),
        AppDestination("plan", "Plan and usage", "?tab=plan", Role.ADMIN, NavGlyph.Chart),
        AppDestination("providers", "Translation providers", "?tab=providers", Role.ADMIN, NavGlyph.Bot, catalogueKey = "project_providers"),
        // `qa` is the second id in this file to repeat with different wording — it is "QA" in
        // `PROJECT_SECTIONS`, the page a translator reads, and "QA rules" here, the checks an admin
        // sets. Hence the `catalogueKey`, and hence its position as the last argument.
        AppDestination("qa", "QA rules", "?tab=qa", Role.ADMIN, NavGlyph.ListChecks, catalogueKey = "project_qa_rules"),
        // Deleting the project is the one thing an admin should not be able to do alone.
        AppDestination("danger", "Danger zone", "?tab=danger", Role.OWNER, NavGlyph.Danger),
    )

    /**
     * The three destinations that belong to every role, drawn when no workspace role is known yet.
     *
     * A null role used to draw an *empty* sidebar: logo, spacer, footer, nothing between them. That
     * is not caution, it is a broken load — and it is the state every page starts in while the
     * session resolves, so the shell flashed a hollow rail on every single navigation. These three
     * are VIEWER-minimum, so drawing them can never offer somebody a page the server refuses.
     */
    private val ROLE_BLIND_IDS = setOf("work", "inbox", "projects")

    fun primaryFor(role: Role?): List<AppDestination> =
        if (role == null) PRIMARY.filter { it.id in ROLE_BLIND_IDS } else PRIMARY.visibleTo(role)

    /**
     * The sidebar's primary destinations, in the two labelled groups the shell draws.
     *
     * The labels are the reason this exists: `YOU` covers the two surfaces that are about the person
     * reading, `WORKSPACE` the three that are about the workspace, and without them the sidebar is
     * five unrelated links whose ordering has to be inferred. The design system specifies the copy
     * and the geometry; this decides which destination belongs to which label.
     *
     * A group whose every destination is hidden by role is dropped rather than drawn empty — a label
     * with nothing under it reads as a failed load.
     */
    fun primaryGroupsFor(role: Role?): List<Pair<String?, List<AppDestination>>> = groupsOf(primaryFor(role))

    /**
     * Every primary destination, grouped, with none of them withheld.
     *
     * The loading shell draws this and replaces the role-gated entries with skeletons in place, so
     * the settled nav **grows into space already held** rather than pushing the footer down when the
     * session resolves. Deriving it from `PRIMARY` rather than from `primaryGroupsFor(OWNER)` keeps
     * a future OWNER-only destination from silently becoming a slot nobody holds.
     */
    fun primaryGroupsWhileResolving(): List<Pair<String?, List<AppDestination>>> = groupsOf(PRIMARY)

    /**
     * Whether this destination is one a role must earn — that is, one the role-blind subset
     * withholds, and therefore one the loading shell holds open with a skeleton rather than a link.
     */
    fun isRoleGated(destination: AppDestination): Boolean = destination.id !in ROLE_BLIND_IDS

    private fun groupsOf(visible: List<AppDestination>): List<Pair<String?, List<AppDestination>>> {
        val grouped = GROUPS.mapNotNull { (label, ids) ->
            val items = ids.mapNotNull { id -> visible.firstOrNull { it.id == id } }
            if (items.isEmpty()) null else label to items
        }
        // Anything in PRIMARY that no group claims. Appended without a label rather than dropped:
        // a destination added above and forgotten here must still be reachable, because silently
        // vanishing from the sidebar is a worse failure than appearing under no heading.
        val claimed = GROUPS.flatMap { it.second }.toSet()
        val orphans = visible.filterNot { it.id in claimed }
        return if (orphans.isEmpty()) grouped else grouped + (null to orphans)
    }

    /**
     * The footer, below the spacer and the divider.
     *
     * `Help` is a page that already exists and that nothing linked to — reachable only by typing the
     * URL. It is the one destination that has to work when the API does not, which makes being
     * unreachable from the shell exactly the wrong way round.
     */
    val help = AppDestination("help", "Help", "/app/help", Role.VIEWER, NavGlyph.Help)

    /**
     * The one destination the command palette offers that no rail draws.
     *
     * `/app/start` teaches the CLI and the first import, and its only inbound links are inside the
     * dashboard's first-run block — which disappears the moment a project exists, on a page a
     * translator cannot open at all. It belongs to the palette rather than the rail for the reason
     * the rail is short: it is a page you want once, not a place you live. It lives *here* rather
     * than as a literal in the palette so `ReachableRoutesTest` can count it as a real door.
     */
    val start = AppDestination("start", "Get started", "/app/start", Role.VIEWER, NavGlyph.Sparkle)

    /**
     * Destinations reachable from the command palette and from no rail at all.
     *
     * The palette's other rows are the rails, [settingsFor] and the project directory, so this is
     * the whole of what it adds. Kept as a list rather than a single value because the next such
     * page will be added to it rather than minted as another literal nothing can see.
     */
    val paletteOnly: List<AppDestination> = listOf(start)

    /**
     * The catalogue key for a group heading.
     *
     * Group labels are plain English literals in the lists below, because these lists are top-level
     * `val`s evaluated at class-init — a `t(…)` written here would answer with its own English
     * fallback for the life of the page. Every surface that draws a heading therefore translates it
     * at the render site, and they must all derive the same key or three of them show English while
     * the fourth shows German. The rule is also implemented in `extract-strings.mjs`
     * (`navGroupSlug`), which is what mints these keys in the first place.
     */
    fun navGroupKey(label: String): String =
        "nav.group." + label.lowercase()
            .map { if (it.isLetterOrDigit()) it else '_' }
            .joinToString("")
            .trim('_')
            .replace(Regex("_+"), "_")

    /** Which label each primary destination sits under. Ids, so a label change is one edit. */
    private val GROUPS: List<Pair<String, List<String>>> = listOf(
        "You" to listOf("work", "inbox"),
        "Workspace" to listOf("dashboard", "projects", "activity", "insights"),
    )

    fun settingsFor(role: Role?): List<AppDestination> = SETTINGS.visibleTo(role)

    /**
     * The settings siblings that answer the same question as the current page.
     *
     * The hub is the full map. Child pages keep only one small local group instead of repeating the
     * entire fourteen-destination inventory above every heading.
     */
    fun settingsGroupFor(role: Role?, currentPath: String): Pair<String, List<AppDestination>>? {
        val normalized = currentPath.substringBefore('?').substringBefore('#').removeSuffix("/")
        if (normalized == "/app/settings") return null
        return settingsHubGroupsFor(role).firstNotNullOfOrNull { (label, destinations) ->
            if (destinations.any { destination ->
                    val path = destination.path.removeSuffix("/")
                    normalized == path || normalized.startsWith("$path/")
                }
            ) {
                label to destinations
            } else {
                null
            }
        }
    }

    /**
     * The settings map: every group this role may see, with its destinations.
     *
     * **One list, three surfaces.** The hub at `/app/settings`, the pill row above every settings
     * child page and the palette's settings rows all draw from here, so a page added to [SETTINGS]
     * appears in all three at once. They used to disagree: the hub hand-wrote thirty links —
     * including five (seats, spend requests, budgets, notifications, documentation) that the model
     * had never heard of — while the pill row rendered the model, so the row above a page could not
     * contain the page you were reading.
     *
     * A group whose destinations are all hidden by role is dropped rather than drawn empty, and a
     * destination no group claims is appended under "More" rather than vanishing — the same
     * fallback [groupsOf] and [projectGroupsFor] keep, and for the same reason.
     */
    fun settingsHubGroupsFor(role: Role?): List<Pair<String, List<AppDestination>>> {
        val visible = settingsFor(role)
        val groups = SETTINGS_GROUPS.mapNotNull { (label, ids) ->
            val destinations = ids.mapNotNull { id -> visible.firstOrNull { it.id == id } }
            if (destinations.isEmpty()) null else label to destinations
        }
        val claimed = SETTINGS_GROUPS.flatMap { it.second }.toSet()
        val orphans = visible.filterNot { it.id in claimed }
        return if (orphans.isEmpty()) groups else groups + ("More" to orphans)
    }

    fun projectSettingsFor(role: Role?): List<AppDestination> = PROJECT_SETTINGS.visibleTo(role)

    /** Groups the project rail by the prepare, translate, verify and ship workflow. */
    fun projectGroupsFor(destinations: List<AppDestination>): List<Pair<String, List<AppDestination>>> {
        val groups = PROJECT_GROUPS.mapNotNull { (label, ids) ->
            val items = ids.mapNotNull { id -> destinations.firstOrNull { it.id == id } }
            if (items.isEmpty()) null else label to items
        }
        val claimed = PROJECT_GROUPS.flatMap { it.second }.toSet()
        val orphans = destinations.filterNot { it.id in claimed }
        return if (orphans.isEmpty()) groups else groups + ("More" to orphans)
    }

    /**
     * Every page of one project, and the role each needs.
     *
     * The paths are suffixes: [projectSectionsFor] joins them onto the slug, so adding a project
     * sub-route is one line here and it appears in the pill row on the next render. That property
     * is the reason the page must not keep its own copy of this list — a second copy drifts, and
     * the louder copy wins.
     */
    private val PROJECT_SECTIONS = listOf(
        AppDestination("overview", "Overview", "", Role.VIEWER, NavGlyph.Grid),
        // The word is `key`. "Strings" survived here and in the trail while the page itself said
        // Keys, so one page had four names across the interface.
        AppDestination("strings", "Keys", "/keys", Role.VIEWER, NavGlyph.Key),
        // The route that consumes eight hours a day, missing from this list until now. It needs a
        // locale and this list only ever had a slug, so `projectSectionsFor` substitutes the
        // project's first target locale into `{locale}` and drops the entry entirely when there
        // isn't one — a link to a blank locale is worse than no link.
        AppDestination("translate", "Translate", "/translate/{locale}", Role.VIEWER, NavGlyph.Globe),
        AppDestination("qa", "QA", "/qa", Role.VIEWER, NavGlyph.Check),
        AppDestination("proposals", "Proposals", "/proposals", Role.VIEWER, NavGlyph.Comment),
        AppDestination("glossary", "Glossary", "/glossary", Role.VIEWER, NavGlyph.BookOpen),
        // The App Store and Google Play listings, reviewed and pushed. VIEWER, because the push
        // history answers "what is live and who put it there" for anyone on the project; the page
        // itself gates every control that reaches a store on owner-or-admin, as the server does.
        AppDestination("stores", "Stores", "/stores", Role.VIEWER, NavGlyph.Box),
        AppDestination("screenshots", "Screenshots", "/screenshots", Role.VIEWER, NavGlyph.Smartphone),
        // Moves files between the repository and the project. Same bar as project settings.
        //
        // The name is `Data`, because that is what the page is called. "Import and export" was the
        // name of `/import-export`, which is now a redirect: the pill said one thing, the header
        // and the breadcrumb said "Data", and the docs sent readers to "each project's Data page" —
        // the same four-names-for-one-page defect the comment above records fixing for Keys.
        AppDestination("data", "Data", "/data", Role.ADMIN, NavGlyph.ArrowUpDown),
        AppDestination("settings", "Settings", "/settings", Role.ADMIN, NavGlyph.Wrench),
    )

    private val SETTINGS_GROUPS = listOf(
        "Workspace" to listOf("general", "workspaces", "members", "providers", "audit", "webhooks"),
        "Money" to listOf("billing", "seats", "usage", "budgets", "spend"),
        "Automation" to listOf("agents", "approvals"),
        "You" to listOf("tokens", "account", "security", "notifications", "close", "help"),
    )

    private val PROJECT_GROUPS = listOf(
        "Prepare" to listOf("overview", "strings", "glossary"),
        "Translate" to listOf("translate", "proposals"),
        "Verify and ship" to listOf("qa", "stores", "screenshots", "data", "settings"),
    )

    /**
     * The sections a role may open, with the slug already joined on.
     *
     * A null role draws the work surfaces rather than nothing, matching [primaryFor]: `visibleTo`
     * returns an empty list for an unresolved session, and an empty pill row reads as a page with
     * no navigation for however long the session takes to answer.
     */
    private val PROJECT_ROLE_BLIND_IDS = setOf("overview", "strings", "qa", "proposals", "glossary", "stores", "screenshots")

    /**
     * @param firstTarget the project's first target locale, substituted into `Translate`'s
     *   `{locale}` segment. Null when the project has none yet — in which case the entry is dropped
     *   rather than drawn pointing at a blank locale, which is a broken link.
     */
    fun projectSectionsFor(role: Role?, slug: String, firstTarget: String?): List<AppDestination> =
        (
            if (role == null) PROJECT_SECTIONS.filter { it.id in PROJECT_ROLE_BLIND_IDS }
            else PROJECT_SECTIONS.visibleTo(role)
        )
            .filter { it.id != "translate" || firstTarget != null }
            .map {
                val path = if (it.id == "translate") it.path.replace("{locale}", firstTarget.orEmpty()) else it.path
                it.copy(path = "/app/projects/$slug$path")
            }

    /**
     * What the rail draws, and whether it is showing a workspace or a project.
     *
     * **A project is somewhere you enter, not a folder you view from outside.** The rail keeps its
     * position — muscle memory for *where* a destination sits survives — and swaps its contents
     * entirely, so a project's own pages occupy the rail rather than a second pill row below the top
     * bar. The pill row is removed on project routes by the same change: two lists of the same
     * destinations, one above the other, is the duplication the project overview spent four commits
     * deleting.
     *
     * @param backPath where the `←` returns to, null at workspace scope. The rail is only "inside"
     *   something when there is an outside to return to.
     */
    data class AppRail(
        val scope: RailScope,
        val destinations: List<AppDestination>,
        val backPath: String? = null,
    )

    enum class RailScope { WORKSPACE, PROJECT }

    /**
     * Which rail this route gets.
     *
     * Keyed off the path rather than off whether a slug was passed, so a caller that happens to hold
     * a slug while rendering a workspace route still gets the workspace rail. `AppRoute.projectSlug`
     * is the single answer to "is this a project route" everywhere else in the shell.
     */
    fun railFor(role: Role?, path: String, firstTarget: String?): AppRail {
        val slug = AppRoute.projectSlug(path)
        return if (slug != null) {
            AppRail(
                scope = RailScope.PROJECT,
                destinations = projectSectionsFor(role, slug, firstTarget),
                backPath = "/app/projects",
            )
        } else {
            AppRail(scope = RailScope.WORKSPACE, destinations = primaryFor(role))
        }
    }

    /**
     * Where the sidebar's Settings link points, or null when this person has no settings page at
     * all — in which case the link is not drawn rather than drawn and dead.
     *
     * **The hub, not the first page in the list.** `/app/settings` is a built index of everything a
     * workspace holds — billing, plans, usage, spend, providers, agents, audit, webhooks, tokens,
     * your account, your security, your notifications — grouped and described. Pointing the only
     * Settings link in the app at `settingsFor(role).first()` routed straight past it onto the
     * Workspace general page, so the index existed and nothing led to it. Every one of those pages
     * was then reachable only by typing its URL, which is why the product reads as having no plan
     * control, no usage, and no account control: it has all of them, behind a door nothing opened.
     *
     * **A null role still gets the link.** The hub is VIEWER-minimum and indexes only what the role
     * opening it may see, and the footer is specified as *real* while the session resolves — a
     * `Settings` link that appears a beat after the page does is a control that moves under a
     * pointer already travelling towards it.
     */
    fun settingsLandingFor(role: Role?): String? =
        if (role != null && settingsFor(role).isEmpty()) null else "/app/settings"

    /**
     * The tab to open when the URL asks for one that this role cannot see.
     *
     * A link shared by an owner ("look at the providers tab") opened by a translator should land on
     * something, not on an empty page or on a tab whose every control is refused.
     */
    fun resolveProjectTab(requested: String?, role: Role?): String? {
        val visible = projectSettingsFor(role)
        return visible.firstOrNull { it.id == requested }?.id ?: visible.firstOrNull()?.id
    }

    /**
     * A role of null is "we do not know yet" — the session is still loading, or this workspace is
     * not one of theirs. It draws nothing here: assuming OWNER until told otherwise would flash an
     * owner's navigation on every page load.
     *
     * The *sidebar* is the one list that does not go empty on null — see [primaryFor], which
     * substitutes the role-blind subset rather than calling this. Settings and project tabs stay
     * empty, because every entry-point they would offer has a management page behind it.
     */
    private fun List<AppDestination>.visibleTo(role: Role?): List<AppDestination> =
        if (role == null) emptyList() else filter { role.atLeast(it.minimumRole) }
}
